import { PromiseClient } from 'hub-http/adapters/promiseClient';
import { sanitize } from 'content-assistance-lib/utils/sanitize';
import getLangLocale from 'I18n/utils/getLangLocale';

export type ContentAssistanceValue =
  | { [property: string]: ContentAssistanceValue }
  | ContentAssistanceValue[]
  | number
  | string
  | boolean;

type FinishReasonTypes = 'stop_sequence' | 'length' | 'unknown';

export type NormalizedContentAssistanceResult = {
  value?: ContentAssistanceValue;
  text?: string;
  flaggedCategories: string[];
  correlationId?: string;
  executionId?: string;
  sanitizedText?: string;
  isPositiveFeedback?: boolean;
  finishReason?: FinishReasonTypes;
};

export type GeneratedContentApiSuccessResult = {
  correlationId: string;
  results: NormalizedContentAssistanceResult[];
  errorMessage: null;
  statusCode: number | null;
};

export type GeneratedContentApiErrorResult = {
  correlationId: string;
  results: null;
  errorMessage: string;
  statusCode: number;
};

type GeneratedContentApiResults =
  | GeneratedContentApiSuccessResult
  | GeneratedContentApiErrorResult;

type AsyncExecutionResponse = {
  executionId: string;
  sessionId: string;
};

export const CONTENT_ASSISTANCE_URL =
  'content-assistance/v2/command-executions';
export const CONTENT_ASSISTANCE_POLLING_URL =
  'content-assistance/v3/command-executions';
const ML_FEEDBACK_URL = 'ml-nlp/v1/feedbacks/text-generation';

export const getExecutionIdUrl = (
  executionId: string,
  basePath = CONTENT_ASSISTANCE_POLLING_URL
) => {
  return `${basePath}/${executionId}`;
};

const RESULT_POLL_TIMEOUT = 200;

// This should be set once per app load with the value
// returned from the first time we hit the content assistance endpoint,
// and then sent to the content assistance end point for every other request made.
let appSessionId: string;

// see https://tools.hubteam.com/lighthouse/framework-app-settings/contentAssistanceCommands/entries
// new commands should be added there
export type ModalCommandsParameters = {
  generate_paragraph_v2: {
    label: string;
    toneOfVoice: string;
  };
  // TODO: build out the rest once i know what they are.
  blog_generate_paragraph_v1: {
    label: string;
    toneOfVoice: string;
  };
  blog_post_suggestions_v1: {
    label: string;
    toneOfVoice: string;
  };
  sales_email_template_v1: {
    emailType: string;
    product: string;
    audience: string;
    reason: string;
    tone: string;
  };
  sales_email_template_v2: {
    emailType: string;
    product: string;
    audience: string;
    reason: string;
    tone: string;
    leadName: string;
    senderName: string;
    senderCompany: string;
  };
};

export type SlashCommandsParameters = {
  blog_inline_slash_commands_generate_paragraph_v2: {
    topic: string;
    editorContext: string;
  };
  blog_inline_slash_commands_generate_an_outline_v1: {
    topic: string;
    editorContext: string;
  };
  blog_inline_slash_commands_generate_conclusion_v2: {
    topic: string;
    editorContext: string;
  };
  page_inline_slash_commands_generate_heading_v1: {
    topic: string;
    editorContext: string;
  };
  page_inline_slash_commands_generate_paragraph_v1: {
    topic: string;
    editorContext: string;
  };
  page_inline_slash_commands_generate_subsection_v1: {
    topic: string;
    editorContext: string;
  };
  marketing_email_inline_slash_commands_generate_paragraph_v2: {
    topic: string;
    editorContext: string;
  };
  marketing_email_inline_slash_commands_generate_heading_v2: {
    topic: string;
    editorContext: string;
  };
  sms_inline_slash_commands_generate_message_v1: {
    topic: string;
    editorContext: string;
  };
};
export type InlineEditorParameters = {
  rephrase_v1: { editorContext: string };
  expand_idea_v1: { editorContext: string };
  summarize_idea_v1: { editorContext: string };
  generate_synononym_v1: { editorContext: string };
  grammar_fix_v1: { editorContext: string };
  change_tone_v1: {
    editorContext: string;
    toneOfVoice: string;
  };
};

export type CommandIds =
  | keyof SlashCommandsParameters
  | keyof InlineEditorParameters
  | keyof ModalCommandsParameters;

export type CommandIdsWithParameters = SlashCommandsParameters &
  InlineEditorParameters &
  ModalCommandsParameters;

type $tsMigrateAny = any;

export class PollingPeriodExceeded extends Error {}

export const fetchContentAssistanceGeneratedContent = async <
  CommandId extends CommandIds,
  Parameters extends CommandIdsWithParameters[CommandId],
>({
  http,
  commandId,
  featureId,
  parameters,
  resultCount = 1,
  // Language code, e.g. `es-es`
  language,
  objectId,
  applicationId,
  basePath,
  pollingInterval = RESULT_POLL_TIMEOUT,
  maxPollingAttempts,
  signal,
}: {
  http: PromiseClient;
  commandId: CommandId | $tsMigrateAny;
  featureId?: number;
  parameters: Parameters | $tsMigrateAny;
  resultCount?: number;
  language?: string;
  objectId?: string;
  applicationId?: string;
  basePath?: string;
  pollingInterval?: number;
  maxPollingAttempts?: number;
  signal?: AbortSignal;
}) => {
  const { executionId, sessionId } = await http.post<AsyncExecutionResponse>(
    basePath || CONTENT_ASSISTANCE_URL,
    {
      data: {
        commandId,
        aiFeatureId: featureId,
        parameters,
        resultCount,
        // Fallback to i18n's language, if none is supplied
        language: language || getLangLocale(),
        sessionId: appSessionId,
        objectId,
        applicationId,
      },
      timeout: 30000,
    }
  );

  if (!appSessionId) {
    appSessionId = sessionId;
  }
  const executionIdUrl = getExecutionIdUrl(executionId, basePath);
  let attempts = 0;

  const poll = async () => {
    if (maxPollingAttempts && attempts >= maxPollingAttempts) {
      return Promise.reject(
        new PollingPeriodExceeded(
          `Polling for executionId: ${executionId} halted after ${attempts} attempts`
        )
      );
    }
    if (signal?.reason) {
      return Promise.resolve({
        correlationId: '',
        errorMessage: null,
        statusCode: null,
        results: [],
      });
    }
    attempts++;

    const resp =
      await http.getWithResponse<GeneratedContentApiResults>(executionIdUrl);
    // 202 means the results aren't ready yet, call again
    if (resp.status === 202) {
      return new Promise<GeneratedContentApiResults>((resolve, reject) => {
        setTimeout(() => {
          return poll()
            .then((result) => resolve(result))
            .catch(reject);
        }, pollingInterval);
      });
    }

    const responseJSON = resp.responseJSON as GeneratedContentApiResults;

    if (responseJSON.statusCode !== null && responseJSON.statusCode >= 400) {
      throw responseJSON;
    }

    return responseJSON;
  };

  return poll().then((data) => {
    const response = data;
    const { results: resultsOrNull, correlationId } = response;
    const results = resultsOrNull || [];

    const normalizedResults: NormalizedContentAssistanceResult[] = [];
    results.forEach((apiResult) => {
      if (apiResult.value) {
        // Sanitize the text
        const unsanitizedValue = apiResult.value;
        const sanitizedValue = sanitize(unsanitizedValue);
        apiResult.text =
          typeof sanitizedValue === 'string' ? sanitizedValue : '';

        // Annotate the result with the correlationId so we can use it for tracking later
        apiResult.correlationId = correlationId;

        normalizedResults.push(apiResult);
      }
    });
    return normalizedResults;
  });
};

export const generateFeedbackAnnotatedResults = (
  generatedContent: NormalizedContentAssistanceResult,
  results: NormalizedContentAssistanceResult[],
  shouldSendPositiveFeedback = true
) => {
  const { text, correlationId } = generatedContent;
  return results.map((result) => {
    const resultCopy = Object.assign({}, result);
    if (
      shouldSendPositiveFeedback &&
      correlationId === resultCopy.correlationId &&
      text === resultCopy.text
    ) {
      // Override the text generated from the endpoint with
      // what the user actually inserted (if they changed some stuff)
      resultCopy.text = text;
      resultCopy.isPositiveFeedback = true;
    } else {
      resultCopy.isPositiveFeedback = false;
    }
    return resultCopy;
  });
};

export const getFeedbackTriggerButtonResults = (
  currentInputText: string,
  generatedContent: NormalizedContentAssistanceResult,
  results: NormalizedContentAssistanceResult[]
) => {
  const { correlationId, text } = generatedContent;
  return results.map((result) => {
    const resultCopy = Object.assign({}, result);
    // Default feedback to negative
    resultCopy.isPositiveFeedback = false;
    if (
      correlationId === resultCopy.correlationId &&
      text === resultCopy.text
    ) {
      // Only successful when it was inserted and not changed
      if (resultCopy.text === currentInputText && results.length === 1) {
        resultCopy.isPositiveFeedback = true;
      } else {
        // Override the text generated from the endpoint with
        // what the user actually inserted (if they changed some stuff)
        resultCopy.text = currentInputText;
      }
    }
    return resultCopy;
  });
};

export const sendFeedbackToML = ({
  http,
  feedbackAnnotatedCompletions,
}: {
  http: PromiseClient;
  feedbackAnnotatedCompletions: NormalizedContentAssistanceResult[];
}) => {
  const allFeedbackRequests: Promise<void>[] = [];

  feedbackAnnotatedCompletions.forEach((feedback) => {
    const { isPositiveFeedback, value, correlationId } = feedback;
    const data = {
      correlationId,
      sessionId: appSessionId,
      feedbacks: [
        {
          value,
          label: isPositiveFeedback,
        },
      ],
    };
    allFeedbackRequests.push(http.post(ML_FEEDBACK_URL, { data }));
  });
  return Promise.all(allFeedbackRequests);
};
